import { type CurrentUser, useAuthStore } from '@/stores/auth';

import {
  apiFetch,
  assertCurrentSession,
  clearSession,
  sessionGeneration,
  tryRefresh,
} from './client';

interface TokenResponse {
  access_token: string;
  token_type: string;
  force_password_change: boolean;
}

/** Fetch the authenticated user's profile. */
export function fetchMe(): Promise<CurrentUser> {
  return apiFetch<CurrentUser>('/auth/me');
}

/** Refresh effective permissions and profile data without rotating tokens. */
export async function refreshCurrentUser(): Promise<CurrentUser> {
  const generation = sessionGeneration();
  const user = await fetchMe();
  assertCurrentSession(generation);
  const currentUser = useAuthStore.getState().user;
  if (currentUser && currentUser.id !== user.id) {
    clearSession();
    throw new DOMException('Session changed', 'AbortError');
  }
  useAuthStore.getState().setSession(useAuthStore.getState().accessToken ?? '', user);
  return user;
}

/** Sign in with credentials and populate the auth store. */
export async function signIn(login: string, password: string): Promise<CurrentUser> {
  clearSession();
  const generation = sessionGeneration();
  try {
    const token = await apiFetch<TokenResponse>(
      '/auth/login',
      { method: 'POST', body: JSON.stringify({ login, password }) },
      false,
    );
    useAuthStore.getState().setAccessToken(token.access_token);
    const user = await fetchMe();
    assertCurrentSession(generation);
    useAuthStore
      .getState()
      .setSession(useAuthStore.getState().accessToken ?? token.access_token, user);
    return user;
  } catch (error) {
    if (generation === sessionGeneration()) clearSession();
    throw error;
  }
}

/** Restore a session from the refresh cookie (called on app boot). */
export async function restoreSession(): Promise<boolean> {
  const generation = sessionGeneration();
  if (!(await tryRefresh())) return false;
  try {
    const user = await fetchMe();
    assertCurrentSession(generation);
    useAuthStore.getState().setSession(useAuthStore.getState().accessToken ?? '', user);
    return true;
  } catch {
    if (generation === sessionGeneration()) clearSession();
    return false;
  }
}

/** Revoke the session server-side and clear local state. */
export async function logout(): Promise<boolean> {
  clearSession();
  const generation = sessionGeneration();
  try {
    await apiFetch<void>('/auth/logout', { method: 'POST' }, false);
    return generation === sessionGeneration();
  } catch (error) {
    if (generation !== sessionGeneration()) return false;
    throw error;
  }
}

/** Change the current user's password. */
export function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return apiFetch<void>('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
}

/** Update the current user's own editable profile fields (display name). */
export async function updateMyProfile(fullName: string): Promise<CurrentUser> {
  const generation = sessionGeneration();
  const user = await apiFetch<CurrentUser>('/me', {
    method: 'PATCH',
    body: JSON.stringify({ full_name: fullName }),
  });
  assertCurrentSession(generation);
  useAuthStore.getState().setSession(useAuthStore.getState().accessToken ?? '', user);
  return user;
}

/** Link a previously-uploaded ``avatar`` file to the current user. */
export async function setAvatar(fileId: string): Promise<CurrentUser> {
  const generation = sessionGeneration();
  const user = await apiFetch<CurrentUser>('/me/avatar', {
    method: 'POST',
    body: JSON.stringify({ file_id: fileId }),
  });
  assertCurrentSession(generation);
  useAuthStore.getState().setSession(useAuthStore.getState().accessToken ?? '', user);
  return user;
}
